import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, find, orderBy, isNaN, isNil, sortBy } from 'lodash';

import {
  getURL,
  getSubType,
  isJoin,
  isPostgreSQL,
  isFiware,
  isAnnotationsLayer,
  isAnnotationsRealTime
} from '../../../api/Layers/layer-operations';
import { LAYER_SUBTYPES, LAYER_TYPES } from '../../../api/Layers/Layers';
import ThematicLayerTable from '../../components/ThematicLayerTable';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import loadTableLayer from '../../../modules/load-table-layer';

const NAV_BAR_HEIGHT = 56;
const HEADER_HEIGHT = 49; // When it is all in the same line
const PAGINATION_HEIGHT = 45;
const CELL_HEIGHT = 35;
const ASCENDENT = 'asc';
const DESCENDENT = 'desc';

class ThematicLayerContainer extends Component {
  state = {
    loading: true,
    tableBody: [],
    tableTitle: [],
    sortIndex: { index: undefined, order: ASCENDENT }
  };

  tableElement = React.createRef();

  componentDidMount() {
    this.loadTable();
    window.addEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(prevProps, prevState) {
    const { layer: prevLayer } = prevProps;
    const { layer } = this.props;
    const { loading } = this.state;
    const { loading: prevLoading } = prevState;

    if (prevLoading && !loading && !isEmpty(layer)) {
      this.updateDimensions();
    }

    if (!isEqual(getURL(layer), getURL(prevLayer))) {
      this.loadTable();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  updateDimensions = () => {
    const { isShared, isAtlas, heightComponent } = this.props;
    const { currentPage, tableBody } = this.state;

    const verticalHeight = isShared ? 0 : NAV_BAR_HEIGHT + HEADER_HEIGHT + CELL_HEIGHT;
    const height = isAtlas
      ? this.tableElement.current.offsetHeight - PAGINATION_HEIGHT
      : window.innerHeight - heightComponent;
    let itemsPerPage = Math.floor((height - verticalHeight - PAGINATION_HEIGHT) / CELL_HEIGHT);
    if (itemsPerPage < 1) itemsPerPage = 1;
    const lastPage = Math.ceil(tableBody.length / itemsPerPage);

    if (currentPage > lastPage) {
      this.setState({ height, currentPage: lastPage });
    } else {
      this.setState({ height });
    }
  };

  loadTable = async () => {
    this.setState({ loading: true });
    const { layer } = this.props;
    const url = getURL(layer);
    if (
      isEmpty(url) &&
      !isJoin(layer) &&
      !isPostgreSQL(layer) &&
      !isFiware(layer) &&
      !isAnnotationsLayer(layer) &&
      !isAnnotationsRealTime(layer)
    ) {
      this.setState({ loading: false, tableBody: [], tableTitle: [] });
    } else {
      try {
        const { tableBody, tableTitle } = await loadTableLayer(layer);

        this.setState({ loading: false, tableBody, tableTitle });
      } catch (err) {
        this.setState({ loading: false, tableBody: [], tableTitle: [] });
        console.error(err);
        handleErrorInClient(err);
      }
    }
  };

  changeSortIndex = newIndex => {
    const { sortIndex, tableBody } = this.state;
    if (!isNil(newIndex)) {
      const newOrder =
        sortIndex.index === newIndex && sortIndex.order === ASCENDENT ? DESCENDENT : ASCENDENT;
      const maxLenght = `${
        sortBy(tableBody, row => `${row[newIndex]}`.length)[tableBody.length - 1][newIndex]
      }`.length;
      const newTableBody = orderBy(
        tableBody,
        row => {
          if (!isNaN(Number(row[newIndex]))) {
            const isPositive = Number(row[newIndex]) >= 0;
            let stringNumber = isPositive
              ? `${Math.abs(Number(row[newIndex]))}`
              : `${1 / Math.abs(Number(row[newIndex]))}`;
            while (stringNumber.length < maxLenght) {
              stringNumber = `0${stringNumber}`;
            }
            return isPositive ? stringNumber : `-${stringNumber}`;
          }
          return row[newIndex];
        },
        newOrder
      );
      this.setState({ tableBody: newTableBody, sortIndex: { index: newIndex, order: newOrder } });
    }
  };

  render() {
    const { loading, tableBody, tableTitle, height, sortIndex } = this.state;
    const { isAtlas, isShared, layer, verHtml } = this.props;
    let { humanValue: layerSubtype } =
      find(LAYER_SUBTYPES.properties, ['value', getSubType(layer)]) || {};

    if (isJoin(layer)) layerSubtype = LAYER_TYPES.properties.JOIN.humanValue;
    return (
      <div ref={this.tableElement} className="h-100">
        <ThematicLayerTable
          layer={layer}
          loading={loading}
          tableTitle={tableTitle}
          tableBody={tableBody}
          layerSubtype={layerSubtype}
          isShared={isShared}
          isAtlas={isAtlas}
          height={height}
          changeSortIndex={this.changeSortIndex}
          sortIndex={sortIndex}
          verHtml={verHtml}
        />
      </div>
    );
  }
}

ThematicLayerContainer.defaultProps = {
  heightComponent: 0,
  isAtlas: false,
  isShared: false,
  verHtml: false
};

ThematicLayerContainer.propTypes = {
  heightComponent: PropTypes.number,
  isAtlas: PropTypes.bool,
  isShared: PropTypes.bool,
  layer: PropTypes.object.isRequired,
  verHtml: PropTypes.bool
};

export default ThematicLayerContainer;
